import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';

const PROJECT_TYPES = [
  { value: 'SAP Business One', label: 'SAP Business One' },
  { value: 'FEX', label: 'FEX' },
  { value: 'LEX', label: 'LEX' },
];

const DeliverableForm = ({ onSubmit, onClose, deliverable }) => {
  const [formData, setFormData] = useState({
    name: '',
    project_type: '',
    description: '',
    content: '',
    stage_id: '',
  });
  const [mergeTags, setMergeTags] = useState([]);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchMergetags = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/mergetags`,
          { withCredentials: true },
        );
        setMergeTags(response.data);
      } catch (error) {
        console.error('Error al cargar mergetags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchStages = async () => {
      try {
        const response = await axios.get(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/stages`,
          { withCredentials: true },
        );
        setStages(response.data);
      } catch (error) {
        console.error('Error al cargar etapas:', error);
      }
    };

    fetchMergetags();
    fetchStages();
  }, []);

  useEffect(() => {
    if (deliverable) {
      setFormData({
        name: deliverable.name || '',
        project_type: deliverable.project_type || '',
        description: deliverable.description || '',
        content:
          deliverable.templates && deliverable.templates[0]
            ? deliverable.templates[0].content || ''
            : '',
        stage_id: deliverable.stage?.id || '',
      });
    } else {
      setFormData({
        name: '',
        project_type: '',
        description: '',
        content: '',
        stage_id: '',
      });
    }
  }, [deliverable]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentInput = (e) => {
    setFormData((prev) => ({
      ...prev,
      content: e,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.project_type) {
      alert('Completa al menos nombre y tipo de proyecto');
      return;
    }

    onSubmit(formData);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
            Nombre*
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
            placeholder="Nombre del entregable"
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
            Tipo de proyecto*
          </label>
          <select
            name="project_type"
            value={formData.project_type}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
          >
            <option value="">Seleccione tipo</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
          Descripción
        </label>
        <input
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
          placeholder="Descripción breve del entregable"
        />
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
          Etapa
        </label>
        <select
          name="stage_id"
          value={formData.stage_id}
          onChange={handleChange}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
        >
          <option value="">Sin etapa</option>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
          Formato editable (plantilla)
        </label>
        {!isLoading && (
          <Editor
            apiKey="6jm9k39nspa60kvxy6uygk9f2ybdg2agvv12pjabvflyt619"
            value={formData.content}
            onEditorChange={handleContentInput}
            init={{
              height: 400,
              menubar: true,
              plugins: [
                'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'link', 'lists', 'media',
                'searchreplace', 'table', 'visualblocks', 'wordcount', 'mergetags'
              ],
              toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | ' +
                'align lineheight | numlist bullist checklist outdent indent | ' +
                'link table mergetags | removeformat',
              tinycomments_mode: 'embedded',
              tinycomments_author: 'Autor',
              mergetags_list: mergeTags.map((t) => ({
                value: t.name,
                title: t.name,
              })),
            }}
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          Usa las variables de <strong>Mergetags</strong> para definir campos que el usuario completará al usar la plantilla.
        </p>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Guardar entregable
        </button>
      </div>
    </form>
  );
};

export default DeliverableForm;
